import { useState, useMemo, useCallback, useEffect, useEffectEvent, useRef } from 'react'
import { useSearchParams } from 'react-router-dom'
import { PageHeader } from '@/components/layout'
import { MeetsBanner } from '@/components/calendario/MeetsBanner'
import { Button } from '@/components/ui/button'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import {
  Video,
  ListPlus,
  CalendarClock,
  Calendar,
  List,
  Download,
  Repeat,
  Eye,
  EyeOff,
  Mail,
  User,
  Users,
} from 'lucide-react'
import { startOfMonth, endOfMonth, format } from 'date-fns'
import { usePermissions } from '@/hooks/usePermissions'
import {
  contarVencimientosPorEstado,
  enPeriodoLista,
  filtrarItemsPorResponsable,
  filtrarVencimientosPorResponsable,
  indexarAsignadosPorCliente,
  indexarReparto,
  resolverScoping,
  sumarCerradosAProximos,
  usuariosConVencimientos,
  vencimientoCorrespondeA,
  type VistaPlanilla,
} from '@/lib/vencimientos-utils'
import { useLocalStorage } from '@/hooks/useLocalStorage'
import { useMapaResponsables, MAPA_VACIO } from '@/core/hooks/useResponsables'
import { useConceptosOrganizacion } from '@/core/hooks/useConceptos'
import { useUsuariosOrg } from '@/hooks/useUsuarios'
import { useCatalogoImpuestos } from '@/hooks/useCatalogoImpuestos'
import { PermisoError, puedeTocarTarea } from '@/lib/permisos-guard'
import { toast } from 'sonner'

import {
  CalendarioMensual,
  EstadisticasVencimientos,
  FiltrosVencimientos,
  MarcarPresentadoDialog,
  VerDetallesVencimientoDialog,
  VencimientosTabla,
  ProgramarAlertaDialog,
  VencimientosDiaDialog,
  RegistrarPagoDialog,
  RegistrarVEPDialog,
  PlanPagoDialog,
  EditarVencimientoDialog,
  CrearEventoDialog,
  EventoDetalleDialog,
  ProrrogaDialog,
  EnviarPresentadoEmailDialog,
} from '@/components/calendario'
import { TareaDetalleDialog, TareaFormDialog, type TareaFormValues } from '@/components/tareas'
import { useCrearTarea } from '@/hooks/useTareas'
import { ClienteDetalleDialog } from '@/components/clientes/ClienteDetalleDialog'
import { ObligacionesRecurrentesTab } from '@/components/calendario/ObligacionesRecurrentesTab'
import { MisMailsTab } from '@/core/components/calendario/MisMailsTab'
import { UltimaSyncWidget } from '@/core/components/calendario/UltimaSyncWidget'

import {
  useProximosVencimientos,
  useVencimientosPorDia,
  useEstadisticasVencimientos,
  useTiposImpuesto,
  useMarcarPresentado,
  useRegistrarPago,
  useRegistrarVEP,
  useMarcarPlanPago,
  useActualizarVencimiento,
  useItemsCalendarioPorDia,
  useCrearEvento,
  useActualizarEvento,
  useEliminarEvento,
  useEventosMes,
  useCrearProrroga,
  useDescartarVencimiento,
  useDescartarVencimientoFisico,
  useRestaurarVencimiento,
  useVencimientosDescartados,
  useVencimientosCerrados,
  useRevertirPresentado,
  useAlertasVencimiento,
  useCrearAlertaVencimiento,
  useEliminarAlertaVencimiento,
} from '@/hooks/useCalendario'
import { claveAlerta } from '@/core/components/calendario/table/vencimientosColumns'
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogFooter,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogAction,
  AlertDialogCancel,
} from '@/components/ui/alert-dialog'
import { useClientes } from '@/hooks/useClientes'
import { useOrganizacion } from '@/core/providers/OrganizacionProvider'
import { useSupabase } from '@/lib/supabase-context'
import { useAuth0 } from '@/lib/auth'
import { documentosService } from '@/core/services/documentosService'
import { guardarEvento } from '@/core/lib/guardarEvento'
import { construirNombreAdjunto } from '@/core/components/calendario/presentacionNaming'
import { formatPeriodo } from '@/lib/formatPeriodo'
import { descargarIcal } from '@/lib/exportarIcal'
import { useIsMobile } from '@/hooks/useIsMobile'

import type {
  VencimientoConCliente,
  RegistrarPagoData,
  RegistrarVEPData,
  MarcarPlanPagoData,
  DatosMaterializacion,
  ProrrogaInsert,
  AlcanceAlerta,
} from '@/types/vencimientos'
import type { MarcarPresentadoData } from '@/core/components/calendario/MarcarPresentadoDialog'
import type { DatosPresentadoEmail } from '@/core/components/calendario/EnviarPresentadoEmailDialog'
import {
  adjuntosDeDocumentos,
  armarDatosPresentadoEmail,
  deducirSaldoTipo,
  nombresAdjuntos,
  presentacionGuardada,
  reintentarVinculosPresentado,
  vincularAdjuntosPresentado,
} from '@/core/components/calendario/notificacionPresentado'
import type { FiltrosVencimientos as FiltrosType } from '@/services/calendarioService'
import type {
  ItemsDelDia,
  EventoCalendario,
  EventoCalendarioInsert,
  EventoCalendarioUpdate,
  TareaProyectada,
} from '@/core/types/calendario'
import type { TareaCompleta, VistaSaldoCliente } from '@/types/database'

export function Calendario() {
  // Auth & Supabase
  const { supabase } = useSupabase()
  const { user } = useAuth0()
  const { puede, esAdmin, emailUsuario } = usePermissions()
  const { catalogo: catalogoImpuestos } = useCatalogoImpuestos()
  const { organizacionId, usuario } = useOrganizacion()
  const usuarioId = usuario?.id ?? null
  const isMobile = useIsMobile()
  // Estado local
  const [mesActual, setMesActual] = useState(new Date())
  // Default: mes actual. Evita cargar el año completo (~999 proyecciones virtuales)
  // de golpe; el usuario amplía con los presets de rango (Hoy/Semana/Mes/30d/90d).
  const [filtros, setFiltros] = useState<FiltrosType>(() => {
    const hoy = new Date()
    return {
      desde: format(startOfMonth(hoy), 'yyyy-MM-dd'),
      hasta: format(endOfMonth(hoy), 'yyyy-MM-dd'),
    }
  })
  const [vistaActiva, setVistaActiva] = useState<'calendario' | 'lista' | 'recurrentes' | 'mis-mails'>(isMobile ? 'lista' : 'calendario')
  const [mostrarPresentados, setMostrarPresentados] = useState(false)

  // Estado de dialogos
  const [dialogMarcar, setDialogMarcar] = useState(false)
  const [dialogDia, setDialogDia] = useState(false)
  const [dialogDetalles, setDialogDetalles] = useState(false)
  const [dialogPago, setDialogPago] = useState(false)
  const [dialogVEP, setDialogVEP] = useState(false)
  const [dialogAlerta, setDialogAlerta] = useState(false)
  const [vencimientoAlerta, setVencimientoAlerta] = useState<VencimientoConCliente | null>(null)
  const [dialogPlanPago, setDialogPlanPago] = useState(false)
  const [dialogEditar, setDialogEditar] = useState(false)
  const [dialogCrearEvento, setDialogCrearEvento] = useState(false)
  const [dialogCrearTarea, setDialogCrearTarea] = useState(false)
  const [dialogDetalleEvento, setDialogDetalleEvento] = useState(false)
  const [dialogDetalleTarea, setDialogDetalleTarea] = useState(false)
  const [tareaSeleccionada, setTareaSeleccionada] = useState<TareaCompleta | null>(null)
  const [dialogProrroga, setDialogProrroga] = useState(false)
  const [dialogEmailPresentado, setDialogEmailPresentado] = useState(false)
  const [datosPresentadoEmail, setDatosPresentadoEmail] = useState<DatosPresentadoEmail | null>(null)
  // Ids of the attachments whose link to the vencimiento failed, by vencimiento id.
  const [vinculosFallidos, setVinculosFallidos] = useState<Record<string, string[]>>({})
  // True while a link retry runs. A second retry at the same time would read the links before the first inserts.
  const reintentoEnCurso = useRef(false)
  const [vencimientoSeleccionado, setVencimientoSeleccionado] = useState<VencimientoConCliente | null>(null)
  const [vencARevertir, setVencARevertir] = useState<VencimientoConCliente | null>(null)
  // ORR-230 fase 5: ficha abierta desde el nombre del cliente en la planilla
  const [clienteFicha, setClienteFicha] = useState<VistaSaldoCliente | null>(null)
  const [eventoSeleccionado, setEventoSeleccionado] = useState<EventoCalendario | null>(null)
  // ORR-228: los cuatro datos del dia abierto viajan juntos y siempre se setean de una.
  // Como estados sueltos se podian desincronizar —abrir un dia y quedarse con las tareas del
  // anterior— y ademas react-doctor los marca como "muchos useState relacionados".
  const [diaAbierto, setDiaAbierto] = useState<{
    fecha: Date | null
    vencimientos: VencimientoConCliente[]
    tareas: TareaCompleta[]
    eventos: EventoCalendario[]
    proyectadas: TareaProyectada[]
  }>({ fecha: null, vencimientos: [], tareas: [], eventos: [], proyectadas: [] })

  // `?nuevo=true` abre el alta de reunión si hay permiso de crear eventos, y se limpia de la URL.
  const [searchParams, setSearchParams] = useSearchParams()
  const abrirAltaDesdeUrl = useEffectEvent(() => {
    if (puede.crearEvento) setDialogCrearEvento(true)
  })
  useEffect(() => {
    if (searchParams.get('nuevo') !== 'true') return
    abrirAltaDesdeUrl()
    setSearchParams({}, { replace: true })
  }, [searchParams, setSearchParams])

  // ORR-178: el filtro "Descartado" cambia la fuente de la lista a la papelera.
  const mostrandoDescartados = !!filtros.estados?.includes('descartado')

  // Queries
  // 365 días para cubrir el año fiscal completo. Los filtros locales (Hoy/Sem/Mes/30d/90d)
  // actúan sobre los datos ya cargados y permiten zoom-in sin refetch.
  const { data: vencimientosProximos = [], isLoading: loadingProximos } = useProximosVencimientos(365)
  const { data: vencimientosDescartados = [], isLoading: loadingDescartados } = useVencimientosDescartados(mostrandoDescartados)
  // Los cerrados del período se ubican por su día de cierre. Sin rango, la Lista muestra solo los próximos.
  // Se piden solo con la pestaña Lista abierta, que es la única que los muestra.
  const {
    data: vencimientosCerrados = [],
    isLoading: loadingCerrados,
    isError: cerradosFallo,
    refetch: reintentarCerrados,
  } = useVencimientosCerrados(filtros.desde, filtros.hasta, !mostrandoDescartados && vistaActiva === 'lista')
  const { data: vencimientosPorDia = [], isLoading: loadingPorDia, isFetching: fetchingPorDia } = useVencimientosPorDia(
    mesActual.getFullYear(),
    mesActual.getMonth() + 1
  )
  const { data: tiposImpuesto = [] } = useTiposImpuesto()
  const { data: clientes = [] } = useClientes()
  // ORR-230 fase 4: el scoping por responsable se ancla en `usuario_id`, no en el string de
  // nombres que emite la vista, y aplica el reparto de conceptos de la fase 3.
  const { data: mapaCargado, isError: mapaFallo, refetch: reintentarMapa } = useMapaResponsables()
  const {
    data: asignacionesCargadas,
    isError: repartoFallo,
    refetch: reintentarReparto,
  } = useConceptosOrganizacion()
  const mapaResponsables = mapaCargado ?? MAPA_VACIO
  const { data: usuariosOrg = [] } = useUsuariosOrg()
  const { data: itemsPorDia = [] } = useItemsCalendarioPorDia(
    mesActual.getFullYear(),
    mesActual.getMonth() + 1
  )
  const { data: eventosMes = [] } = useEventosMes(
    mesActual.getFullYear(),
    mesActual.getMonth() + 1
  )

  const reparto = useMemo(
    () => indexarReparto(asignacionesCargadas ?? []),
    [asignacionesCargadas],
  )

  const asignadosPorCliente = useMemo(
    () => indexarAsignadosPorCliente(asignacionesCargadas ?? []),
    [asignacionesCargadas],
  )

  // ORR-367 — el interruptor "Míos / Todo el estudio". Arranca en "Míos", que es lo que pidió
  // Lisandro: su planilla operativa filtrada por lo que él hace. La preferencia se recuerda por
  // navegador; si `localStorage` no está disponible, el hook cae en el default, que es el correcto.
  // La clave lleva el usuario: un navegador compartido haría que el segundo en entrar heredara la
  // vista del primero (Qodo #5). Mientras la sesión no resolvió, la clave es la anónima y el hook
  // re-lee cuando el id aparece.
  const [vistaPlanilla, setVistaPlanilla] = useLocalStorage<VistaPlanilla>(
    `calendario-vista-planilla:${usuarioId ?? 'anon'}`,
    'mios',
  )

  // `esAdmin` sale del rol; `resolverScoping` lo cruza con el interruptor. Es el ÚNICO punto donde
  // se decide el alcance: la tabla, el grid mensual y el dialog del día leen todos este objeto, así
  // que las tres superficies siguen al interruptor sin lógica repetida.
  const scoping = useMemo(
    () =>
      resolverScoping(
        { esAdmin, usuarioId, mapaResponsables, reparto },
        vistaPlanilla,
      ),
    [esAdmin, usuarioId, mapaResponsables, reparto, vistaPlanilla],
  )

  // El scoping necesita el mapa de responsables y el reparto. Sin alguno de los dos no se puede
  // decidir de quién es cada vencimiento, y el filtro no deja ninguno. La planilla, el grid y las
  // tarjetas muestran la carga mientras llegan y un aviso de error si alguno falló. Nunca muestran
  // los vencimientos sin filtrar.
  const scopingIncompleto =
    !scoping.esAdmin &&
    !!usuarioId &&
    (mapaCargado === undefined || asignacionesCargadas === undefined)
  const scopingFallido = scopingIncompleto && (mapaFallo || repartoFallo)
  const scopingCargando = scopingIncompleto && !scopingFallido

  const reintentarScoping = () => {
    if (mapaFallo) void reintentarMapa()
    if (repartoFallo) void reintentarReparto()
  }

  // ORR-382: las tarjetas cuentan lo mismo que muestra la planilla. Va DESPUÉS del scoping y no
  // arriba con el resto de los hooks porque lo necesita como argumento.
  const { data: estadisticas, isLoading: loadingEstadisticas } =
    useEstadisticasVencimientos(scoping)

  // Pre-filtrar vencimientos por responsable según rol (ORR-205: helper compartido con
  // el grid mensual y el widget de Inicio). Owner/Admin ve todo; asistente solo sus clientes.
  const vencimientosFiltradosPorRol = useMemo(
    () =>
      filtrarVencimientosPorResponsable(
        sumarCerradosAProximos(
          vencimientosProximos as VencimientoConCliente[],
          vencimientosCerrados as VencimientoConCliente[],
        ),
        scoping,
      ),
    [vencimientosProximos, vencimientosCerrados, scoping],
  )

  // "Míos" en el calendario, igual que en la lista de tareas: la tarea asignada a mí.
  const scopingTareas = useMemo(() => ({ emailUsuario }), [emailUsuario])

  // ORR-296: los ítems unificados (vencimientos + eventos + tareas) alimentan tanto la grilla
  // como el dialog del día, y venían SIN scopear — `obtenerItemsPorMes` filtra por organización
  // y nada más. Mientras el grid solo pintaba puntitos no se notaba; ahora la celda muestra
  // impuesto y cliente, así que un asistente estaría leyendo vencimientos ajenos.
  // Nota: el enforcement server-side de esto está en duda, ver ORR-297.
  const itemsPorDiaFiltrados = useMemo(
    () => filtrarItemsPorResponsable(itemsPorDia, scoping, scopingTareas),
    [itemsPorDia, scoping, scopingTareas],
  )

  // ORR-205: el grid mensual también se scopea por responsable (antes traía todos).
  // Los datos vienen agrupados por día (VencimientosDelDia), así que se filtra el array
  // interno de cada día y se recalculan los badges de conteo con el mismo criterio.
  // ORR-367: el guard lee `scoping.esAdmin`, no el `esAdmin` del rol. Con el rol crudo el grid
  // ignoraba el interruptor y quedaba mostrando todo el estudio mientras la tabla ya filtraba.
  const vencimientosPorDiaFiltrados = useMemo(() => {
    if (scoping.esAdmin || !usuarioId) return vencimientosPorDia
    return vencimientosPorDia.map((dia) => {
      const vencimientos = filtrarVencimientosPorResponsable(dia.vencimientos, scoping)
      return { ...dia, vencimientos, ...contarVencimientosPorEstado(vencimientos) }
    })
  }, [vencimientosPorDia, usuarioId, scoping])

  // ORR-178: con el filtro "Descartado" activo, la fuente de la lista es la papelera.
  // ORR-367 (Qodo #4): la papelera se scopea con el MISMO criterio que la lista. Venía cruda —
  // `obtenerVencimientosDescartados` filtra por organización y nada más—, así que en "Míos" la
  // tabla se abría a los descartados de todo el estudio y el interruptor parecía no hacer nada.
  const fuenteVencimientos = useMemo(
    () =>
      mostrandoDescartados
        ? filtrarVencimientosPorResponsable(
            vencimientosDescartados as VencimientoConCliente[],
            scoping,
          )
        : vencimientosFiltradosPorRol,
    [mostrandoDescartados, vencimientosDescartados, scoping, vencimientosFiltradosPorRol],
  )

  // Opciones del multi-select: a quiénes les corresponde alguno de los vencimientos a la vista.
  // Salen de `usuarios_brainhub` para tener el nombre real; el valor es el id.
  // ORR-370: la pertenencia la define `usuariosConVencimientos`, el mismo criterio que aplica el
  // filtro de abajo. Recorrer solo `mapaResponsables` dejaba fuera del desplegable a quien tiene
  // conceptos repartidos sin ser responsable del cliente.
  const responsablesUnicos = useMemo(() => {
    const conVencimientos = usuariosConVencimientos(
      vencimientosFiltradosPorRol,
      mapaResponsables,
      reparto,
    )

    const opciones: { id: string; nombre: string }[] = []
    for (const u of usuariosOrg) {
      if (conVencimientos.has(u.id)) opciones.push({ id: u.id, nombre: u.nombre || u.email })
    }

    return opciones.sort((a, b) => a.nombre.localeCompare(b.nombre, 'es'))
  }, [vencimientosFiltradosPorRol, mapaResponsables, reparto, usuariosOrg])

  // Mapear clientes para el filtro. Los responsables van por id para que el filtro cruzado
  // (elegir un responsable acota la lista de clientes) hable el mismo idioma que el multi-select.
  // ORR-370: incluye a los asignados por concepto, o elegir a uno de ellos como responsable dejaba
  // el selector de clientes en "Sin resultados" — la opción existía y no se podía combinar.
  const clientesParaFiltro = useMemo(
    () =>
      clientes.map((c: { id?: string | null; nombre?: string | null }) => {
        const id = c.id ?? ''
        return {
          id,
          nombre: c.nombre ?? '',
          responsablesIds: [
            ...new Set([...(mapaResponsables.get(id) ?? []), ...(asignadosPorCliente.get(id) ?? [])]),
          ],
        }
      }),
    [clientes, mapaResponsables, asignadosPorCliente],
  )

  // ORR-230 fase 5: el nombre del cliente en la planilla abre su ficha en el tab de vencimientos,
  // que muestra TODOS los del cliente (la planilla muestra los que le tocan al usuario).
  const handleAbrirFichaCliente = useCallback(
    (v: VencimientoConCliente) => {
      const enLista = clientes.find((c: { id?: string | null }) => c.id === v.cliente_id)
      // Sin el cliente en la lista cargada, alcanza con lo que el vencimiento ya trae: el dialog
      // completa el resto con `useCliente(id)`. Un click sin efecto seria un bug invisible.
      const minimo = { id: v.cliente_id, nombre: v.cliente_nombre, cuit: v.cliente_cuit }
      setClienteFicha((enLista ?? minimo) as unknown as VistaSaldoCliente)
    },
    [clientes],
  )

  // Mutations
  const marcarPresentado = useMarcarPresentado()
  const registrarPago = useRegistrarPago()
  const registrarVEP = useRegistrarVEP()
  const marcarPlanPago = useMarcarPlanPago()
  const actualizarVencimiento = useActualizarVencimiento()
  const crearEvento = useCrearEvento()
  const crearTarea = useCrearTarea()
  const actualizarEvento = useActualizarEvento()
  const eliminarEvento = useEliminarEvento()
  const crearProrroga = useCrearProrroga()
  const descartarVencimiento = useDescartarVencimiento()
  const descartarVencimientoFisico = useDescartarVencimientoFisico()
  const restaurarVencimiento = useRestaurarVencimiento()
  const revertirPresentado = useRevertirPresentado()
  const crearAlerta = useCrearAlertaVencimiento()
  const eliminarAlerta = useEliminarAlertaVencimiento()

  // ORR-232: las alertas del usuario se traen de una vez y se cruzan en memoria. Una query
  // por fila sería inviable: la lista pasa de las 1400 filas.
  const { data: alertas = [] } = useAlertasVencimiento()
  const alertasPorAncla = useMemo(() => {
    const map = new Map<string, string>()
    for (const a of alertas) {
      if (a.alcance === 'todos_los_siguientes') continue
      const ancla = a.calendario_afip_id || a.vencimiento_fiscal_id
      if (ancla) {
        map.set(
          claveAlerta({
            cliente_id: a.cliente_id,
            id: a.vencimiento_fiscal_id,
            calendario_afip_id: a.calendario_afip_id,
          }),
          a.id
        )
      }
    }
    return map
  }, [alertas])
  const alertasAncla = useMemo(() => new Set(alertasPorAncla.keys()), [alertasPorAncla])

  // Handlers
  const handleExportarIcal = () => {
    // Combine vencimientos from porDia data + eventos del mes
    const vencimientosExport = vencimientosPorDia.flatMap(d => d.vencimientos)
    descargarIcal(vencimientosExport, eventosMes as EventoCalendario[])
    toast.success('Calendario exportado')
  }

  const handleMarcarPresentado = (vencimiento: VencimientoConCliente) => {
    setVencimientoSeleccionado(vencimiento)
    setDialogMarcar(true)
  }

  const handleVerDetalles = (vencimiento: VencimientoConCliente) => {
    setVencimientoSeleccionado(vencimiento)
    setDialogDetalles(true)
  }

  const handleRegistrarPago = (vencimiento: VencimientoConCliente) => {
    setVencimientoSeleccionado(vencimiento)
    setDialogPago(true)
  }

  const handleRegistrarVEP = (vencimiento: VencimientoConCliente) => {
    setVencimientoSeleccionado(vencimiento)
    setDialogVEP(true)
  }

  const handlePlanPago = (vencimiento: VencimientoConCliente) => {
    setVencimientoSeleccionado(vencimiento)
    setDialogPlanPago(true)
  }

  const handleEditar = (vencimiento: VencimientoConCliente) => {
    setVencimientoSeleccionado(vencimiento)
    setDialogEditar(true)
  }

  const handleConfirmarEditar = async (id: string, datos: Record<string, unknown>) => {
    await actualizarVencimiento.mutateAsync({ id, datos })
    toast.success('Vencimiento actualizado correctamente')
  }

  // Helper: obtener datos de materialización si el vencimiento es virtual
  const getMaterializacion = (v: VencimientoConCliente | null): DatosMaterializacion | undefined => {
    if (!v?.es_virtual || !v.calendario_afip_id || !v.cliente_id) return undefined
    return { calendario_afip_id: v.calendario_afip_id, cliente_id: v.cliente_id }
  }

  const handleConfirmarPago = async (id: string, datos: RegistrarPagoData) => {
    await registrarPago.mutateAsync({ id, datos, materializacion: getMaterializacion(vencimientoSeleccionado) })
    toast.success('Pago registrado correctamente')
  }

  const handleConfirmarVEP = async (id: string, datos: RegistrarVEPData) => {
    await registrarVEP.mutateAsync({ id, datos, materializacion: getMaterializacion(vencimientoSeleccionado) })
    toast.success('VEP registrado correctamente')
  }

  const handleConfirmarPlanPago = async (id: string, datos: MarcarPlanPagoData) => {
    await marcarPlanPago.mutateAsync({ id, datos, materializacion: getMaterializacion(vencimientoSeleccionado) })
    toast.success('Plan de pago registrado correctamente')
  }

  const handleConfirmarPresentado = async (id: string, datos: MarcarPresentadoData) => {
    const { adjuntos, ...datosPresentado } = datos
    const adjuntosSubidos: Array<{ docId: string; nombre: string; path: string }> = []

    // Step 1: Upload adjuntos FIRST (before marking as presented)
    if (adjuntos?.length && supabase && vencimientoSeleccionado?.cliente_id) {
      const clienteId = vencimientoSeleccionado.cliente_id
      const userEmail = user?.email || 'sistema'

      for (const adjunto of adjuntos) {
        try {
          // ORR-179: renombrar el adjunto a "{tipo} - {impuesto} - {cliente}.ext"
          // tanto en el guardado (BD) como en el envío por email.
          const nombreAdjunto = construirNombreAdjunto(
            adjunto.file.name,
            adjunto.categoria,
            adjunto.descripcion,
            vencimientoSeleccionado.tipo_impuesto ?? '',
            vencimientoSeleccionado.cliente_nombre ?? '',
            formatPeriodo(vencimientoSeleccionado.periodo),
          )
          const documento = await documentosService.subir(supabase, adjunto.file, clienteId, userEmail, {
            categoria: adjunto.categoria,
            nombre: nombreAdjunto,
            descripcion: adjunto.descripcion,
          }, organizacionId)
          adjuntosSubidos.push({ docId: documento.id, nombre: nombreAdjunto, path: documento.archivo_path })
        } catch (error) {
          console.error('Error al subir adjunto:', error)
          const detalle = error instanceof Error ? error.message : 'Error desconocido'
          toast.error(`Error al subir adjunto: ${detalle}. No se marcó como presentado.`)
          throw error
        }
      }
    }

    // Step 2: Mark as presented (only if adjuntos uploaded OK)
    let vencimientoMarcado: Awaited<ReturnType<typeof marcarPresentado.mutateAsync>> | undefined
    try {
      vencimientoMarcado = await marcarPresentado.mutateAsync({
        id,
        datos: datosPresentado,
        materializacion: getMaterializacion(vencimientoSeleccionado),
      })
      toast.success('Vencimiento marcado como presentado')
    } catch (error) {
      if (error instanceof PermisoError) {
        toast.error('No tenés permisos para marcar vencimientos como presentados')
      } else {
        toast.error('Error al marcar como presentado. Si persiste, contactá soporte.')
        console.error('[Calendario] Error marcando presentado:', error)
      }
      throw error
    }

    // Step 3: Link uploaded documents to the stored row the RPC returns.
    // The view id of a virtual vencimiento has no row, so it is never used as a fallback.
    const idReal = vencimientoMarcado?.id
    let fallidos: string[] = []
    if (adjuntosSubidos.length > 0 && supabase) {
      if (idReal) {
        fallidos = await vincularAdjuntosPresentado(supabase, idReal, adjuntosSubidos.map((a) => a.docId))
      } else {
        console.error('[Calendario] El RPC de presentado no devolvió la fila; los adjuntos no se vincularon')
        toast.error('Se marcó como presentado, pero los adjuntos no quedaron vinculados al vencimiento.')
      }
    }

    // The notification dialog always opens after presenting. Without an email in the record it explains how to load it.
    if (!vencimientoSeleccionado) return
    const datosEmail = armarDatosPresentadoEmail(
      { ...vencimientoSeleccionado, id: idReal ?? vencimientoSeleccionado.id },
      {
        fecha_presentacion: datosPresentado.fecha_presentacion,
        numero_presentacion: datosPresentado.numero_presentacion,
        monto_determinado: datosPresentado.monto_determinado ?? undefined,
        saldoTipo: deducirSaldoTipo(datosPresentado.monto_determinado),
        forma_pago: datosPresentado.forma_pago,
        observaciones: datosPresentado.observaciones,
        mensaje_libre: datosPresentado.mensaje_libre,
      },
      adjuntosSubidos,
    )
    setVinculosFallidos((prev) => ({ ...prev, [datosEmail.vencimiento.id]: fallidos }))
    setDatosPresentadoEmail(datosEmail)
    setDialogEmailPresentado(true)
  }

  /**
   * Retries the failed links of a presented vencimiento, reports the result and returns the ids that still fail.
   * While another retry runs it does nothing and returns the same ids.
   */
  const reintentarVinculos = async (vencimientoId: string, docIds: string[]): Promise<string[]> => {
    if (!supabase || reintentoEnCurso.current) return docIds
    reintentoEnCurso.current = true
    try {
      const pendientes = await reintentarVinculosPresentado(supabase, vencimientoId, docIds)
      if (pendientes.length === 0) {
        toast.success('Los adjuntos quedaron vinculados al vencimiento')
      } else {
        toast.error(
          pendientes.length === 1
            ? '1 adjunto sigue sin vincular. Probá de nuevo.'
            : `${pendientes.length} adjuntos siguen sin vincular. Probá de nuevo.`,
        )
      }
      return pendientes
    } catch (error) {
      console.error('[Calendario] Error reintentando el vínculo de adjuntos:', error)
      toast.error('No se pudo reintentar el vínculo de los adjuntos. Probá de nuevo.')
      return docIds
    } finally {
      reintentoEnCurso.current = false
    }
  }

  /**
   * Persistent warning with a retry action for attachments still unlinked after the notification dialog closed.
   * The action keeps its own vencimiento and ids, because a later presentation replaces the dialog state.
   * The warning stays open while the retry runs: it closes when every link is stored and updates in place if some still fail.
   */
  const avisarVinculosPendientes = (datos: DatosPresentadoEmail, docIds: string[]) => {
    const { id, tipo_impuesto, cliente_nombre } = datos.vencimiento
    const toastId = `vinculos-presentado-${id}`
    const nombres = nombresAdjuntos(datos.adjuntosSubidos, docIds).join(', ')
    const detalle =
      docIds.length === 1
        ? 'no quedó vinculado al vencimiento. Un reenvío de la notificación no lo va a encontrar.'
        : 'no quedaron vinculados al vencimiento. Un reenvío de la notificación no los va a encontrar.'
    toast.warning(`${tipo_impuesto} de ${cliente_nombre}: ${nombres} ${detalle}`, {
      id: toastId,
      duration: Infinity,
      action: {
        label: 'Reintentar',
        onClick: async (event) => {
          event.preventDefault()
          const pendientes = await reintentarVinculos(id, docIds)
          if (pendientes.length === 0) toast.dismiss(toastId)
          else avisarVinculosPendientes(datos, pendientes)
        },
      },
    })
  }

  // Ids whose link failed for the vencimiento of the notification dialog.
  const fallidosDialog = datosPresentadoEmail ? (vinculosFallidos[datosPresentadoEmail.vencimiento.id] ?? []) : []

  // The result only replaces the entry of its own vencimiento, and only while that entry exists.
  const handleReintentarVinculosDialog = async () => {
    if (!datosPresentadoEmail) return
    const { id } = datosPresentadoEmail.vencimiento
    const pendientes = await reintentarVinculos(id, fallidosDialog)
    setVinculosFallidos((prev) => (id in prev ? { ...prev, [id]: pendientes } : prev))
  }

  // Closing the notification dialog drops the entry of its vencimiento and leaves the persistent warning if links are pending.
  const handleDialogEmailPresentado = (open: boolean) => {
    setDialogEmailPresentado(open)
    if (open || !datosPresentadoEmail) return
    const { id } = datosPresentadoEmail.vencimiento
    setVinculosFallidos(({ [id]: _cerrado, ...resto }) => resto)
    if (fallidosDialog.length > 0) avisarVinculosPendientes(datosPresentadoEmail, fallidosDialog)
  }

  // Unmounting the page with the notification dialog open leaves the persistent warning if links are pending.
  const avisarPendientesAlDesmontar = useEffectEvent(() => {
    if (dialogEmailPresentado && datosPresentadoEmail && fallidosDialog.length > 0) {
      avisarVinculosPendientes(datosPresentadoEmail, fallidosDialog)
    }
  })
  useEffect(() => () => avisarPendientesAlDesmontar(), [])

  /** Opens the notification dialog of a presented vencimiento with the documents linked to it. */
  const handleReenviarNotificacion = async (vencimiento: VencimientoConCliente) => {
    if (!supabase || !vencimiento.id) return
    try {
      const documentos = await documentosService.obtenerPorEntidad(supabase, 'vencimiento', vencimiento.id, 'comprobante')
      setDatosPresentadoEmail(
        armarDatosPresentadoEmail(vencimiento, presentacionGuardada(vencimiento), adjuntosDeDocumentos(documentos)),
      )
      setDialogEmailPresentado(true)
    } catch (error) {
      console.error('[Calendario] Error leyendo los adjuntos del vencimiento:', error)
      toast.error('No se pudieron leer los adjuntos del vencimiento. Probá de nuevo.')
    }
  }

  const handleCrearProrroga = async (datos: ProrrogaInsert) => {
    await crearProrroga.mutateAsync({ ...datos, organizacion_id: organizacionId ?? null })
  }

  const handleCrearEvento = (evento: EventoCalendarioInsert) =>
    guardarEvento(
      crearEvento.mutateAsync({ ...evento, organizacion_id: organizacionId ?? null }),
      'Reunión creada correctamente',
    )

  // ORR-295: alta rápida de tarea desde el calendario. Sin adjuntos a propósito —
  // el upload diferido vive en la página de Tareas, que es donde se los espera.
  const handleCrearTarea = async (data: TareaFormValues) => {
    // `TareaFormDialog` no espera esta promesa, así que un rechazo acá quedaría sin
    // manejar y el dialog se cerraría igual, perdiendo lo cargado. El toast de error
    // ya lo emite el `onError` de `useCrearTarea`.
    try {
      await crearTarea.mutateAsync(data)
      toast.success('Tarea creada correctamente')
      setDialogCrearTarea(false)
    } catch (error) {
      console.error('Error al crear tarea desde el calendario:', error)
    }
  }

  const handleActualizarEvento = (id: string, datos: EventoCalendarioUpdate) =>
    guardarEvento(actualizarEvento.mutateAsync({ id, datos }), 'Evento actualizado correctamente')

  const handleEliminarEvento = async (id: string) => {
    await eliminarEvento.mutateAsync(id)
    toast.success('Evento eliminado correctamente')
  }

  const handleProgramarAlerta = (vencimiento: VencimientoConCliente) => {
    setVencimientoAlerta(vencimiento)
    setDialogAlerta(true)
  }

  /**
   * ORR-232: el ancla se resuelve igual que en descartar — calendario_afip_id cuando la fila
   * viene del calendario AFIP, el id propio cuando es recurrente o manual. Para el alcance
   * "todos los siguientes" la alerta se ancla al impuesto, no a una fila.
   */
  const handleConfirmarAlerta = async (datos: { dias_antes: number; alcance: AlcanceAlerta }) => {
    const v = vencimientoAlerta
    if (!v?.cliente_id) {
      toast.error('No se puede programar una alerta sobre este vencimiento')
      return
    }
    if (datos.alcance === 'todos_los_siguientes' && !v.impuesto_id) {
      toast.error('Este vencimiento no tiene impuesto asociado')
      return
    }
    try {
      await crearAlerta.mutateAsync({
        cliente_id: v.cliente_id,
        alcance: datos.alcance,
        dias_antes: datos.dias_antes,
        calendario_afip_id: datos.alcance === 'solo_proximo' ? v.calendario_afip_id ?? null : null,
        vencimiento_fiscal_id:
          datos.alcance === 'solo_proximo' && !v.calendario_afip_id ? v.id ?? null : null,
        impuesto_id: datos.alcance === 'todos_los_siguientes' ? v.impuesto_id ?? null : null,
      })
      toast.success(
        datos.alcance === 'solo_proximo'
          ? `Te avisamos ${datos.dias_antes === 0 ? 'el día del vencimiento' : `${datos.dias_antes} día(s) antes`}`
          : 'Te avisamos en cada período de este impuesto'
      )
    } catch (error) {
      console.error('Error al programar alerta:', error)
      toast.error('No se pudo programar la alerta')
    }
  }

  const handleQuitarAlerta = async (alertaId: string) => {
    try {
      await eliminarAlerta.mutateAsync(alertaId)
      toast.success('Alerta quitada')
    } catch (error) {
      console.error('Error al quitar alerta:', error)
      toast.error('No se pudo quitar la alerta')
    }
  }

  const handleDescartarVencimiento = async (vencimiento: VencimientoConCliente) => {
    if (!puede.descartarVencimiento) {
      toast.error('No tenés permiso para descartar vencimientos')
      return
    }
    // ORR-184: descartar opera por (calendario_afip_id, cliente_id), presentes tanto en
    // virtuales como en materializados. No exige es_virtual (a diferencia de
    // getMaterializacion, que se usa en los flujos de pago/presentación).
    // ORR-234: los que NO vienen del calendario AFIP (recurrentes, manuales) no tienen ese id;
    // esos se descartan por el id de su propia fila. Las dos vías devuelven el id que espera
    // restaurar_vencimiento, así que el "Deshacer" de abajo sirve igual para ambas.
    const { calendario_afip_id: calendarioAfipId, cliente_id: clienteId, id: idFila } = vencimiento
    if (!clienteId) {
      toast.error('No se puede descartar este vencimiento')
      return
    }
    try {
      const id = calendarioAfipId
        ? await descartarVencimiento.mutateAsync({ calendarioAfipId, clienteId })
        : idFila
          ? await descartarVencimientoFisico.mutateAsync(idFila)
          : null
      if (!id) {
        toast.error('No se puede descartar este vencimiento')
        return
      }
      toast.success('Vencimiento descartado', {
        action: {
          label: 'Deshacer',
          onClick: () => {
            restaurarVencimiento.mutate(id, {
              onSuccess: () => toast.success('Vencimiento restaurado'),
            })
          },
        },
      })
    } catch (error) {
      if (error instanceof PermisoError) toast.error(error.message)
      else toast.error('No se pudo descartar el vencimiento')
    }
  }

  const handleRestaurarVencimiento = async (vencimiento: VencimientoConCliente) => {
    if (!puede.restaurarVencimiento) {
      toast.error('No tenés permiso para restaurar vencimientos')
      return
    }
    if (!vencimiento.id) return
    try {
      await restaurarVencimiento.mutateAsync(vencimiento.id)
      toast.success('Vencimiento restaurado')
    } catch (error) {
      if (error instanceof PermisoError) toast.error(error.message)
      else toast.error('No se pudo restaurar el vencimiento')
    }
  }

  const handleRevertirPresentado = (vencimiento: VencimientoConCliente) => {
    if (!puede.editarVencimiento) {
      toast.error('No tenés permiso para revertir presentaciones')
      return
    }
    setVencARevertir(vencimiento)
  }

  const handleConfirmarRevertir = async () => {
    if (!vencARevertir?.id) return
    try {
      await revertirPresentado.mutateAsync(vencARevertir.id)
      toast.success('Presentación revertida. El vencimiento volvió a pendiente.')
    } catch (error) {
      if (error instanceof PermisoError) toast.error(error.message)
      else toast.error(error instanceof Error ? error.message : 'No se pudo revertir la presentación')
    } finally {
      setVencARevertir(null)
    }
  }

  const handleVerEvento = (evento: EventoCalendario) => {
    setEventoSeleccionado(evento)
    setDialogDetalleEvento(true)
  }

  const handleVerTarea = (tarea: TareaCompleta) => {
    setTareaSeleccionada(tarea)
    setDialogDetalleTarea(true)
  }

  // ORR-228: un solo camino, cualquiera sea la mezcla del dia. Antes habia dos atajos —"solo
  // eventos" y "solo tareas" abrian el detalle directo— y todo lo demas caia en un dialog que
  // solo sabia mostrar vencimientos. Por eso una tarea desaparecia al compartir dia con un
  // vencimiento. Pedido textual de Lisandro: el mismo recuadro "ya sea que este sola o comparta
  // fecha", y desde ahi se despliega.
  const handleSeleccionarDiaUnificado = (fecha: Date, items: ItemsDelDia) => {
    const vencimientosDelDia: VencimientoConCliente[] = []
    const tareasDelDia: TareaCompleta[] = []
    const eventosDelDia: EventoCalendario[] = []
    // ORR-331: las proyectadas van en su propia lista. No son filas, así que no pueden entrar a
    // `tareas` — pero tampoco pueden quedar afuera, o un día que solo las tiene abre vacío.
    const proyectadasDelDia: TareaProyectada[] = []
    for (const item of items.items) {
      if (item.tipo === 'vencimiento') {
        vencimientosDelDia.push(item.datos_originales as unknown as VencimientoConCliente)
      } else if (item.tipo === 'tarea') {
        tareasDelDia.push(item.datos_originales as unknown as TareaCompleta)
      } else if (item.tipo === 'evento') {
        eventosDelDia.push(item.datos_originales as EventoCalendario)
      } else if (item.tipo === 'tarea_proyectada') {
        proyectadasDelDia.push(item.datos_originales as unknown as TareaProyectada)
      }
    }
    setDiaAbierto({
      fecha,
      vencimientos: vencimientosDelDia,
      tareas: tareasDelDia,
      eventos: eventosDelDia,
      proyectadas: proyectadasDelDia,
    })
    setDialogDia(true)
  }

  // Este camino solo trae vencimientos. Setear el objeto entero es lo que evita arrastrar
  // las tareas del ultimo dia abierto, sin tener que acordarse de limpiarlas.
  const handleSeleccionarDia = (fecha: Date, vencimientos: VencimientoConCliente[]) => {
    setDiaAbierto({ fecha, vencimientos, tareas: [], eventos: [], proyectadas: [] })
    setDialogDia(true)
  }

  const handleCambiarMes = (fecha: Date) => {
    setMesActual(fecha)
  }

  const defaultEstadisticas = {
    pendientes: 0,
    presentados: 0,
    vencidos: 0,
    pagados: 0,
    enPlanPago: 0,
    proximaSemana: 0,
    proximoMes: 0,
    vencenEstaSemana: 0,
    vencenEsteMes: 0,
    presentadosEsteMes: 0,
    pagadosEsteMes: 0,
  }

  return (
    <div className="flex flex-col h-full">
      <PageHeader
        title="Calendario Fiscal"
        description="Gestión de vencimientos y declaraciones juradas"
      />

      <main className="flex-1 p-4 md:p-6 pb-4 space-y-4">
        {/* Acciones */}
        <div className="flex flex-wrap gap-2">
          {puede.crearEvento && (
            <Button variant="outline" size="sm" onClick={() => setDialogCrearEvento(true)}>
              <Video className="h-4 w-4 mr-2" />
              <span className="hidden sm:inline">Nueva</span> reunión
            </Button>
          )}

          {puede.crearTarea && (
            <Button variant="outline" size="sm" onClick={() => setDialogCrearTarea(true)}>
              <ListPlus className="h-4 w-4 mr-2" />
              <span className="hidden sm:inline">Nueva</span> tarea
            </Button>
          )}
          {puede.editarVencimiento && (
            <Button variant="outline" size="sm" onClick={() => setDialogProrroga(true)}>
              <CalendarClock className="h-4 w-4 mr-2" />
              Prórrogas
            </Button>
          )}
          <Button variant="outline" size="sm" className="hidden sm:flex" onClick={handleExportarIcal}>
            <Download className="h-4 w-4 mr-2" />
            Exportar iCal
          </Button>
        </div>

        {/* Reuniones de hoy y mañana: la grilla muestra que las hay, no a qué hora ni con qué
            enlace. Se oculta sola si no hay ninguna. */}
        <MeetsBanner />

        {/* Widget última sync ARCA (banner si > 14 días) */}
        <UltimaSyncWidget />

        {scopingFallido && (
          <div className="rounded-md border border-destructive/50 bg-destructive/10 p-4 text-sm flex items-center justify-between gap-4">
            <span className="text-destructive">
              No se pudo cargar a quién le corresponde cada vencimiento. Hasta reintentar, los
              vencimientos no se muestran.
            </span>
            <Button variant="outline" size="sm" className="h-7 text-xs" onClick={reintentarScoping}>
              Reintentar
            </Button>
          </div>
        )}

        {/* Estadisticas */}
        <EstadisticasVencimientos
          estadisticas={estadisticas || defaultEstadisticas}
          isLoading={loadingEstadisticas || scopingCargando}
        />

        {/* ORR-367 — interruptor "Míos / Todo el estudio". Va ARRIBA de las solapas y no dentro de
            Lista (Qodo #1): el scoping alcanza también al grid mensual y al dialog del día, así que
            un control escondido en otra solapa dejaba esos datos filtrados sin nada que lo explique.
            Solo para admin: a un colaborador `resolverScoping` lo mantiene scopeado igual, aunque el
            control se renderice por error. */}
        {esAdmin && (
          <div className="flex items-center gap-1 rounded-md border p-0.5 w-fit">
            <Button
              variant={vistaPlanilla === 'mios' ? 'secondary' : 'ghost'}
              size="sm"
              onClick={() => setVistaPlanilla('mios')}
              className="h-7 text-xs"
              aria-pressed={vistaPlanilla === 'mios'}
            >
              <User className="h-3.5 w-3.5 mr-1.5" />
              Míos
            </Button>
            <Button
              variant={vistaPlanilla === 'estudio' ? 'secondary' : 'ghost'}
              size="sm"
              onClick={() => setVistaPlanilla('estudio')}
              className="h-7 text-xs"
              aria-pressed={vistaPlanilla === 'estudio'}
            >
              <Users className="h-3.5 w-3.5 mr-1.5" />
              Todo el estudio
            </Button>
          </div>
        )}

        {/* Tabs de vista */}
        <Tabs
          value={vistaActiva}
          onValueChange={(v) => setVistaActiva(v as 'calendario' | 'lista' | 'recurrentes' | 'mis-mails')}
        >
          <div className="overflow-x-auto -mx-4 px-4 md:mx-0 md:px-0">
            <TabsList className="w-max md:w-auto">
              {/* Calendar tab hidden on mobile - mobile uses Lista as default */}
              <TabsTrigger value="calendario" className="hidden md:flex">
                <Calendar className="h-4 w-4 mr-2" />
                Calendario
              </TabsTrigger>
              <TabsTrigger value="lista">
                <List className="h-4 w-4 mr-1 md:mr-2" />
                Lista
              </TabsTrigger>
              <TabsTrigger value="recurrentes" className="hidden sm:flex">
                <Repeat className="h-4 w-4 mr-2" />
                Recurrentes
              </TabsTrigger>
              <TabsTrigger value="mis-mails" className="hidden sm:flex">
                <Mail className="h-4 w-4 mr-2" />
                Mis mails
              </TabsTrigger>
            </TabsList>
          </div>

          <TabsContent value="calendario" className="mt-4">
            {/* Calendario mensual - desktop only. Ancho completo desde ORR-293: el panel
                "Vencimientos Activos" que ocupaba el tercio derecho leía `fuenteVencimientos`,
                exactamente la misma fuente que la pestaña "Lista", que además tiene filtros y
                todas las acciones. Era una segunda ventana al mismo dato, no información extra. */}
            <div className="hidden md:block">
              <CalendarioMensual
                vencimientosPorDia={vencimientosPorDiaFiltrados}
                itemsPorDia={itemsPorDiaFiltrados}
                mesActual={mesActual}
                onCambiarMes={handleCambiarMes}
                onSeleccionarDia={handleSeleccionarDia}
                onSeleccionarDiaUnificado={handleSeleccionarDiaUnificado}
                isLoading={loadingPorDia || scopingCargando}
                isFetching={fetchingPorDia}
              />
            </div>
          </TabsContent>

          <TabsContent value="lista" className="mt-4 space-y-4">
            {/* Filtros */}
            <FiltrosVencimientos
              filtros={filtros}
              onFiltrosChange={setFiltros}
              tiposImpuesto={tiposImpuesto as string[]}
              responsables={esAdmin ? responsablesUnicos : []}
              clientes={clientesParaFiltro}
            />

            {cerradosFallo && !mostrandoDescartados && (
              <div className="rounded-md border border-destructive/50 bg-destructive/10 p-4 text-sm flex items-center justify-between gap-4">
                <span className="text-destructive">
                  No se pudieron cargar los presentados del período. La lista muestra solo los
                  pendientes y los próximos.
                </span>
                <Button variant="outline" size="sm" className="h-7 text-xs" onClick={() => void reintentarCerrados()}>
                  Reintentar
                </Button>
              </div>
            )}

            {/* Con "Míos" y cero resultados la planilla nace vacía y parece rota. Medido en PROD el
                09/09: 2 de los 7 usuarios no tienen ningún vencimiento propio. */}
            {esAdmin && vistaPlanilla === 'mios' && !mostrandoDescartados && !scopingIncompleto && fuenteVencimientos.length === 0 && (
              <div className="rounded-md border border-dashed p-4 text-sm text-muted-foreground flex items-center justify-between gap-4">
                <span>No tenés vencimientos asignados a tu nombre.</span>
                <Button variant="outline" size="sm" className="h-7 text-xs" onClick={() => setVistaPlanilla('estudio')}>
                  <Users className="h-3.5 w-3.5 mr-1.5" />
                  Ver todo el estudio
                </Button>
              </div>
            )}

            {/* Toggle presentados */}
            <div className="flex items-center">
              <Button
                variant={mostrarPresentados ? 'secondary' : 'outline'}
                size="sm"
                onClick={() => setMostrarPresentados(!mostrarPresentados)}
                className="h-8 text-xs"
              >
                {mostrarPresentados ? (
                  <EyeOff className="h-3.5 w-3.5 mr-1.5" />
                ) : (
                  <Eye className="h-3.5 w-3.5 mr-1.5" />
                )}
                {mostrarPresentados ? 'Ocultar presentados' : 'Mostrar presentados'}
              </Button>
            </div>

            {/* Tabla densa de vencimientos */}
            <VencimientosTabla
              onRegistrarPago={handleRegistrarPago}
              onRegistrarVEP={handleRegistrarVEP}
              onPlanPago={handlePlanPago}
              onDescartar={puede.descartarVencimiento ? handleDescartarVencimiento : undefined}
              onRestaurar={puede.restaurarVencimiento ? handleRestaurarVencimiento : undefined}
              onRevertirPresentado={puede.editarVencimiento ? handleRevertirPresentado : undefined}
              onReenviarNotificacion={puede.editarVencimiento ? handleReenviarNotificacion : undefined}
              onProgramarAlerta={handleProgramarAlerta}
              onAbrirCliente={handleAbrirFichaCliente}
              alertasAncla={alertasAncla}
              catalogo={catalogoImpuestos}
              vencimientos={fuenteVencimientos.filter((v: VencimientoConCliente) => {
                // ORR-81: Ocultar presentados por defecto
                if (!mostrarPresentados) {
                  const estadoEfectivo = (v as unknown as { estado_efectivo?: string }).estado_efectivo ?? v.estado
                  if (estadoEfectivo === 'presentado') return false
                }

                // Filtro por tipos de impuesto (multi-select)
                if (filtros.tipos_impuesto?.length && !filtros.tipos_impuesto.includes(v.tipo_impuesto as string)) {
                  return false
                }

                // Filtro por estados (multi-select, usar estado_efectivo calculado en la vista)
                if (filtros.estados?.length) {
                  const estadoEfectivo = (v as unknown as { estado_efectivo?: string }).estado_efectivo ?? v.estado
                  if (!filtros.estados.includes(estadoEfectivo as string)) {
                    return false
                  }
                }

                // Filtro por responsables (multi-select por `usuario_id`). Muestra lo que ve el
                // responsable elegido, reparto incluido: sin eso la planilla del admin nunca
                // reflejaría a quién le tocó cada concepto.
                if (filtros.responsables?.length) {
                  const correspondeAAlguno = filtros.responsables.some((id: string) =>
                    vencimientoCorrespondeA(v, id, mapaResponsables, reparto),
                  )
                  if (!correspondeAAlguno) return false
                }

                // Filtro por clientes (multi-select)
                if (filtros.clientes_ids?.length && !filtros.clientes_ids.includes(v.cliente_id as string)) {
                  return false
                }

                // Rango del período: un cerrado entra por su día de cierre, el resto por su vencimiento.
                if (!enPeriodoLista(v, filtros.desde, filtros.hasta)) return false

                return true
              })}
              onMarcarPresentado={handleMarcarPresentado}
              onVerDetalles={handleVerDetalles}
              titulo={mostrandoDescartados ? 'Vencimientos descartados' : 'Vencimientos'}
              isLoading={(mostrandoDescartados ? loadingDescartados : loadingProximos || loadingCerrados) || scopingCargando}
            />
          </TabsContent>

          <TabsContent value="recurrentes" className="mt-4">
            <ObligacionesRecurrentesTab />
          </TabsContent>

          <TabsContent value="mis-mails" className="mt-4">
            <MisMailsTab />
          </TabsContent>
        </Tabs>
      </main>

      {/* Dialog: Marcar como presentado */}
      <MarcarPresentadoDialog
        open={dialogMarcar}
        onOpenChange={setDialogMarcar}
        vencimiento={vencimientoSeleccionado ? {
          id: vencimientoSeleccionado.id ?? '',
          tipo_impuesto: (vencimientoSeleccionado.tipo_impuesto ?? '') as string,
          cliente_nombre: vencimientoSeleccionado.cliente_nombre ?? undefined,
          periodo: (vencimientoSeleccionado.periodo ?? '') as string,
          fecha_vencimiento: (vencimientoSeleccionado.fecha_vencimiento ?? '') as string,
          origen_tipo: vencimientoSeleccionado.origen_tipo ?? null,
          // ORR-238: sin esto el diálogo no puede saber que es una recategorización
          // y cae al mensaje libre. El campo es opcional en el tipo, así que
          // omitirlo NO da error de compilación: apaga la feature en silencio.
          obligacion_recurrente_id: vencimientoSeleccionado.obligacion_recurrente_id ?? null,
        } : null}
        onConfirmar={handleConfirmarPresentado}
      />

      {/* Dialog: Ver detalles de vencimiento */}
      <VerDetallesVencimientoDialog
        open={dialogDetalles}
        onOpenChange={setDialogDetalles}
        vencimiento={vencimientoSeleccionado}
        onRegistrarPago={handleRegistrarPago}
        onRegistrarVEP={handleRegistrarVEP}
        onPlanPago={handlePlanPago}
        onMarcarPresentado={handleMarcarPresentado}
        onEditar={handleEditar}
      />

      {/* Dialog: Vencimientos del dia seleccionado */}
      <VencimientosDiaDialog
        open={dialogDia}
        onOpenChange={setDialogDia}
        fecha={diaAbierto.fecha}
        vencimientos={diaAbierto.vencimientos}
        onMarcarPresentado={handleMarcarPresentado}
        onVerDetalles={handleVerDetalles}
        tareas={diaAbierto.tareas}
        eventos={diaAbierto.eventos}
        proyectadas={diaAbierto.proyectadas}
        onVerTarea={handleVerTarea}
        onVerEvento={handleVerEvento}
      />

      {/* Dialog: Programar alerta (ORR-232) */}
      <ProgramarAlertaDialog
        open={dialogAlerta}
        onOpenChange={setDialogAlerta}
        vencimiento={vencimientoAlerta}
        alertaExistenteId={vencimientoAlerta ? alertasPorAncla.get(claveAlerta(vencimientoAlerta)) ?? null : null}
        onConfirmar={handleConfirmarAlerta}
        onQuitar={handleQuitarAlerta}
      />

      {/* Dialog: Registrar pago */}
      <RegistrarPagoDialog
        open={dialogPago}
        onOpenChange={setDialogPago}
        vencimiento={vencimientoSeleccionado}
        onConfirmar={handleConfirmarPago}
      />

      {/* Dialog: Registrar VEP */}
      <RegistrarVEPDialog
        open={dialogVEP}
        onOpenChange={setDialogVEP}
        vencimiento={vencimientoSeleccionado}
        onConfirmar={handleConfirmarVEP}
      />

      {/* Dialog: Plan de pago */}
      <PlanPagoDialog
        open={dialogPlanPago}
        onOpenChange={setDialogPlanPago}
        vencimiento={vencimientoSeleccionado}
        onConfirmar={handleConfirmarPlanPago}
      />

      {/* Dialog: Editar vencimiento */}
      <EditarVencimientoDialog
        open={dialogEditar}
        onOpenChange={setDialogEditar}
        vencimiento={vencimientoSeleccionado}
        onConfirmar={handleConfirmarEditar}
      />

      {/* Dialog: Crear evento */}
      <CrearEventoDialog
        open={dialogCrearEvento}
        onOpenChange={setDialogCrearEvento}
        onCrear={handleCrearEvento}
      />

      {/* Dialog: Crear tarea (ORR-295) */}
      <TareaFormDialog
        open={dialogCrearTarea}
        onOpenChange={setDialogCrearTarea}
        onSubmit={handleCrearTarea}
        isLoading={crearTarea.isPending}
      />

      {/* Dialog: Detalle/editar evento */}
      <EventoDetalleDialog
        open={dialogDetalleEvento}
        onOpenChange={setDialogDetalleEvento}
        evento={eventoSeleccionado}
        onActualizar={handleActualizarEvento}
        onEliminar={handleEliminarEvento}
      />

      {/* Dialog: Detalle de tarea (ORR-228, solo lectura desde acá) */}
      <TareaDetalleDialog
        open={dialogDetalleTarea}
        onOpenChange={setDialogDetalleTarea}
        tarea={tareaSeleccionada}
        // ORR-347 / ORR-381: mismo gate que en Tareas, y ahora mira de quién es la tarea.
        // Antes alcanzaba con poder crear tareas para mover una ajena desde acá.
        canChangeEstado={!!tareaSeleccionada && puedeTocarTarea(tareaSeleccionada, puede, emailUsuario)}
      />

      {/* Dialog: Prórrogas */}
      <ProrrogaDialog
        open={dialogProrroga}
        onOpenChange={setDialogProrroga}
        onConfirmar={handleCrearProrroga}
        organizacionId={organizacionId}
      />

      {/* Dialog: Email post-presentación */}
      <EnviarPresentadoEmailDialog
        open={dialogEmailPresentado}
        onOpenChange={handleDialogEmailPresentado}
        datos={datosPresentadoEmail}
        adjuntosSinVincular={nombresAdjuntos(datosPresentadoEmail?.adjuntosSubidos ?? [], fallidosDialog)}
        onReintentarVinculos={handleReintentarVinculosDialog}
      />

      {/* Confirmación: revertir presentación */}
      <AlertDialog open={!!vencARevertir} onOpenChange={(open) => { if (!open) setVencARevertir(null) }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Revertir la presentación?</AlertDialogTitle>
            <AlertDialogDescription>
              El vencimiento{vencARevertir?.cliente_nombre ? ` de ${vencARevertir.cliente_nombre}` : ''} volverá
              a <strong>pendiente</strong> y reaparecerá en la planilla. Se borran fecha de presentación,
              número de trámite y monto determinado. Los documentos adjuntos seguirán vinculados al vencimiento.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={revertirPresentado.isPending}>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleConfirmarRevertir} disabled={revertirPresentado.isPending}>
              {revertirPresentado.isPending ? 'Revirtiendo…' : 'Revertir presentación'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Ficha del cliente abierta desde la planilla, directo al tab de vencimientos */}
      <ClienteDetalleDialog
        open={!!clienteFicha}
        onOpenChange={(open) => { if (!open) setClienteFicha(null) }}
        cliente={clienteFicha}
        initialTab="vencimientos"
      />
    </div>
  )
}
